import { useMemo, useState } from "react";
import { CHORD_DESCRIPTION, CHORD_GROUPS, CHORD_INTERVALS, CHORD_SUFFIX, invert, inversionName, spellChord, type ChordQuality } from "../../../theory/chords";
import { TUNINGS } from "../../../theory/fretboard";
import { keyName, keyTonics } from "../../../theory/key";
import { intervalShortName, nameToPitchClass, pitchClass, pitchClassName } from "../../../theory/notes";
import { SCALE_TYPES, getScale, stepPattern, type ScaleType } from "../../../theory/scales";
import { useLive, useProject, useServices } from "../../context";
import { Keyboard } from "../Keyboard";
import { Panel, Select, Toggle } from "../controls";
import { Fretboard } from "./Fretboard";

const ROOTS = ["C", "C#", "Db", "D", "Eb", "E", "F", "F#", "Gb", "G", "Ab", "A", "Bb", "B"] as const;
const KEYBOARD_START = 48; // C3
const ROOT_OCTAVE_BASE = 60; // roots are played from C4–B4

function intervalLabel(semitones: number): string {
  return semitones === 14 ? "M9" : intervalShortName(semitones);
}

/** Explore scales and chords: spelled notes, intervals, keyboard and fretboard, playback. */
export function Explorer() {
  const { controller } = useServices();
  const projectKey = useProject((p) => p.song.key);
  const held = useLive((s) => s.heldNotes);
  const [root, setRoot] = useState<string>(projectKey.tonic);
  const [kind, setKind] = useState<"scale" | "chord">("scale");
  const [scaleType, setScaleType] = useState<ScaleType>(projectKey.mode === "minor" ? "minor" : "major");
  const [quality, setQuality] = useState<ChordQuality>("maj");
  const [inversion, setInversion] = useState(0);
  const [tuningId, setTuningId] = useState(TUNINGS[0]!.id);

  const rootPc = nameToPitchClass(root) ?? 0;
  const rootMidi = ROOT_OCTAVE_BASE + rootPc;
  const view = useMemo(() => {
    if (kind === "scale") {
      const s = getScale(root, scaleType);
      return {
        title: `${root} ${scaleType}`,
        names: s.notes,
        intervals: s.semitones.map(intervalLabel),
        pattern: stepPattern(s.semitones).join(" – "),
        pcs: s.pitchClasses,
        playSteps: [...s.semitones, 12, ...[...s.semitones].reverse()].map((st) => [rootMidi + st]),
        voicing: [] as number[],
        description: null as string | null,
      };
    }
    const names = spellChord(root, quality);
    const iv = CHORD_INTERVALS[quality];
    const voicing = invert(iv.map((i) => rootMidi + i), inversion);
    return {
      title: `${root}${CHORD_SUFFIX[quality]}${inversion > 0 ? ` (${inversionName(inversion % iv.length)})` : ""}`,
      names,
      intervals: iv.map(intervalLabel),
      pattern: null as string | null,
      pcs: iv.map((i) => pitchClass(rootPc + i)),
      playSteps: voicing.map((n) => [n]),
      voicing,
      description: `${root} ${CHORD_DESCRIPTION[quality]}`,
    };
  }, [kind, root, scaleType, quality, inversion, rootMidi, rootPc]);

  const nameByPc = useMemo(() => new Map(view.pcs.map((pc, i) => [pc, view.names[i]!])), [view]);
  const highlight = useMemo(() => new Set(view.pcs), [view]);
  const heldSet = useMemo(() => new Set(held), [held]);
  const tuning = TUNINGS.find((t) => t.id === tuningId)!;
  const asKey = kind === "scale" && (scaleType === "major" || scaleType === "minor")
    ? { tonic: keyTonics(scaleType)[rootPc]!, mode: scaleType }
    : null;

  return (
    <Panel title="Scale & chord explorer" className="span-all">
      <div className="controls-row explorer-controls">
        <Select label="Root" value={root} options={ROOTS.map((r) => ({ value: r, label: r }))} onChange={setRoot} />
        <div className="segmented" role="group" aria-label="Explore">
          <Toggle pressed={kind === "scale"} onChange={() => setKind("scale")}>Scales</Toggle>
          <Toggle pressed={kind === "chord"} onChange={() => setKind("chord")}>Chords</Toggle>
        </div>
        {kind === "scale" ? (
          <Select label="Scale" value={scaleType} options={SCALE_TYPES.map((s) => ({ value: s, label: s }))} onChange={setScaleType} />
        ) : (
          <>
            <span className="select">
              <label htmlFor="explorer-quality">Chord</label>
              <select id="explorer-quality" value={quality} onChange={(e) => { setQuality(e.currentTarget.value as ChordQuality); setInversion(0); }}>
                {CHORD_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.qualities.map((q) => <option key={q} value={q}>{`${root}${CHORD_SUFFIX[q]} — ${CHORD_DESCRIPTION[q]}`}</option>)}
                  </optgroup>
                ))}
              </select>
            </span>
            <Select label="Inversion" value={inversion} options={CHORD_INTERVALS[quality].map((_, i) => ({ value: i, label: inversionName(i) }))} onChange={setInversion} />
          </>
        )}
      </div>

      <div className="explorer-info" aria-live="polite">
        <h3>{view.title}</h3>
        {view.description && <p>{view.description}.</p>}
        <dl className="facts">
          <dt>Notes</dt><dd className="notes">{view.names.join(" ")}</dd>
          <dt>Intervals from root</dt><dd>{view.intervals.join(" ")}</dd>
          {view.pattern && (<><dt>Step pattern</dt><dd>{view.pattern} <span className="hint">(W = whole tone, H = half tone)</span></dd></>)}
          {kind === "chord" && (<><dt>Voicing</dt><dd>{view.voicing.map((n) => `${nameByPc.get(pitchClass(n)) ?? pitchClassName(n)}${Math.floor(n / 12) - 1}`).join(" ")}</dd></>)}
        </dl>
        <div className="controls-row">
          <button type="button" className="primary" onClick={() => controller.previewSequence(view.playSteps, 0.28, { transpose: false })}>
            {kind === "scale" ? "▶ Play scale" : "▶ Arpeggio"}
          </button>
          {kind === "chord" && <button type="button" onClick={() => controller.previewNotes(view.voicing, 1.5, 0.7, { transpose: false })}>▶ Play chord</button>}
          {asKey && (
            <button type="button" onClick={() => controller.updateProject((p) => ({ ...p, song: { ...p.song, key: asKey } }))}
              disabled={asKey.tonic === projectKey.tonic && asKey.mode === projectKey.mode}>
              Use {keyName(asKey)} as project key
            </button>
          )}
        </div>
      </div>

      <Keyboard
        startNote={KEYBOARD_START} octaves={3} highlight={highlight} tonicPc={rootPc} held={heldSet}
        nameOf={(n) => `${nameByPc.get(pitchClass(n)) ?? pitchClassName(n)}${Math.floor(n / 12) - 1}`}
        onNoteOn={(n) => controller.noteOn(n)} onNoteOff={(n) => controller.noteOff(n)}
      />

      <div className="controls-row">
        <Select label="Fretboard" value={tuningId} options={TUNINGS.map((t) => ({ value: t.id, label: t.label }))} onChange={setTuningId} />
      </div>
      <Fretboard tuning={tuning} frets={12} highlight={nameByPc} rootPc={rootPc} onPlay={(m) => controller.previewNotes([m], 0.8, 0.7, { transpose: false })} />
      <p className="hint">The root is bold. Explorer playback ignores song transposition so you hear exactly what is shown.</p>
    </Panel>
  );
}
